import React, { useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { ShouldCostCoverageData } from "../../../types/contractDashboard";
import styles from "./shouldCostCoverage.module.scss";

interface ShouldCostCoverageProps {
  data: ShouldCostCoverageData[];
}

// Color mapping for status
const STATUS_COLORS = {
  good: "#10b981",
  warning: "#f59e0b",
  critical: "#ef4444",
} as const;

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;
  const value = payload[0].value as number;

  const getStatusIcon = (status: string) => {
    if (status === "good")
      return <CheckCircleIcon sx={{ fontSize: 16, color: STATUS_COLORS.good }} />;
    if (status === "warning")
      return <WarningIcon sx={{ fontSize: 16, color: STATUS_COLORS.warning }} />;
    return <ErrorIcon sx={{ fontSize: 16, color: STATUS_COLORS.critical }} />;
  };

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "4px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            borderRadius: "2px",
            backgroundColor: payload[0].color,
          }}
        />
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
          Coverage:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
        >
          {value}%
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          paddingTop: "4px",
          borderTop: "1px solid #f0f0f0",
          marginTop: "4px",
        }}
      >
        {getStatusIcon(data.status)}
        <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
          Status: {data.status === "good" ? "Good" : data.status === "warning" ? "Warning" : "Critical"}
        </Typography>
      </Box>
    </Box>
  );
};

const ShouldCostCoverage: React.FC<ShouldCostCoverageProps> = ({
  data: propData,
}) => {
  // Memoize chart data transformation
  const chartData = useMemo(() => {
    return propData.map((item) => ({
      category: item.category,
      coveragePct: item.coveragePct,
      status: item.status === "good" ? "good" : item.status === "warning" ? "warning" : "critical",
    }));
  }, [propData]);

  // Memoize color function
  const getBarColor = useCallback((status: string) => {
    return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.critical;
  }, []);

  // Memoize X-axis tick formatter
  const xAxisTickFormatter = useCallback((value: number) => {
    return `${value}%`;
  }, []);

  if (!propData || propData.length === 0) {
    return (
      <Box className={styles.container} sx={{ width: "100%" }}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Should-Cost / Market Benchmark Coverage
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            % of strategic categories with market-based pricing models.
          </Typography>
        </Box>
        <Box className={styles.chartContainer}>
          <Typography
            variant="body2"
            style={{ textAlign: "center", padding: "20px" }}
          >
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container} sx={{ width: "100%" }}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          Should-Cost / Market Benchmark Coverage
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          % of strategic categories with market-based pricing models.
        </Typography>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 130, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={xAxisTickFormatter}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              ticks={[0, 20, 40, 60, 80, 100]}
            />
            <YAxis
              type="category"
              dataKey="category"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
              width={125}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            <Bar
              dataKey="coveragePct"
              name="Coverage"
              animationDuration={800}
              radius={[0, 4, 4, 0]}
              barSize={35}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(ShouldCostCoverage);

